import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RoleProtectedRoute } from '@/components/RoleProtectedRoute'
import { AppLayout } from '@/components/layouts/AppLayout'
import { AuthLayout } from '@/components/layouts/AuthLayout'

// Auth pages (eagerly loaded — needed on first visit)
import { SignInPage, SignUpPage, AuthCallbackPage, EmailVerifiedPage } from '@/pages/auth'

// Static pages (small, eagerly loaded)
import { TermsPage } from '@/pages/TermsPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { HelpPage } from '@/pages/HelpPage'

// Lazy-loaded route groups
const OnboardingPage = lazy(() => import('@/pages/onboarding').then(m => ({ default: m.OnboardingPage })))
const PaidOnboardingPage = lazy(() =>
  import('@/pages/onboarding').then((m) => ({ default: m.PaidOnboardingPage })),
)
const SupportPersonOnboardingPage = lazy(() =>
  import('@/pages/onboarding').then((m) => ({ default: m.SupportPersonOnboardingPage })),
)

// User pages
const DashboardPage = lazy(() => import('@/pages/user/DashboardPage').then(m => ({ default: m.DashboardPage })))
const FreeVideoPage = lazy(() => import('@/pages/user/FreeVideoPage').then(m => ({ default: m.FreeVideoPage })))
const VoiceMemosPage = lazy(() => import('@/pages/user/VoiceMemosPage').then(m => ({ default: m.VoiceMemosPage })))
const SupporterLinkPage = lazy(() => import('@/pages/user/SupporterLinkPage').then(m => ({ default: m.SupporterLinkPage })))
const UpgradePage = lazy(() => import('@/pages/user/UpgradePage').then(m => ({ default: m.UpgradePage })))
const PaymentSuccessPage = lazy(() => import('@/pages/user/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })))
const ProgrammePage = lazy(() => import('@/pages/user/ProgrammePage').then(m => ({ default: m.ProgrammePage })))
const AssessmentsPage = lazy(() => import('@/pages/user/AssessmentsPage').then(m => ({ default: m.AssessmentsPage })))
const AccountSettingsPage = lazy(() => import('@/pages/user/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })))

// Supporter pages
const SupporterDashboardPage = lazy(() => import('@/pages/supporter').then(m => ({ default: m.SupporterDashboardPage })))
const ClientsPage = lazy(() => import('@/pages/supporter').then(m => ({ default: m.ClientsPage })))
const ClientDetailPage = lazy(() => import('@/pages/supporter').then(m => ({ default: m.ClientDetailPage })))
const ContentLibraryPage = lazy(() => import('@/pages/supporter').then(m => ({ default: m.ContentLibraryPage })))

// Admin pages
const AdminDashboardPage = lazy(() => import('@/pages/admin').then(m => ({ default: m.AdminDashboardPage })))
const AdminUsersPage = lazy(() => import('@/pages/admin').then(m => ({ default: m.UsersPage })))
const AdminVideosPage = lazy(() => import('@/pages/admin').then(m => ({ default: m.VideosPage })))
const AdminSupportersPage = lazy(() => import('@/pages/admin').then(m => ({ default: m.SupportersPage })))
const AdminPaymentsPage = lazy(() => import('@/pages/admin').then(m => ({ default: m.PaymentsPage })))
const AdminSettingsPage = lazy(() => import('@/pages/admin').then(m => ({ default: m.AdminSettingsPage })))

// ============================================
// LOADING FALLBACK
// ============================================

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="w-8 h-8 border-4 border-[#E3DED2] border-t-[#D4AF5A] rounded-full animate-spin" />
    </div>
  )
}

// ============================================
// PUBLIC PAGES
// ============================================

function HomePage() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#1B2A4A] bg-cover bg-center bg-no-repeat px-4"
      style={{ backgroundImage: 'url(/auth-bg.jpg)' }}
    >
      <div
        className="w-full max-w-xl text-center bg-[#0F1C2E]/55 backdrop-blur-md border border-white/15 rounded-3xl px-8 py-10 sm:px-12 sm:py-12"
        style={{ boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)' }}
      >
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
          Drive Without Fear
        </h1>
        <p className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed">
          Evidence-based therapy to help you overcome driving anxiety and reclaim your freedom.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link
            to="/auth/sign-in"
            className="px-8 py-3 bg-[#D4AF5A] text-[#0F1C2E] rounded-full hover:brightness-105 transition-all font-semibold shadow-[0_8px_24px_rgba(15,28,46,0.3)]"
          >
            Log In
          </Link>
          <Link
            to="/auth/sign-up"
            className="px-8 py-3 bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white rounded-full hover:bg-white/20 transition-all font-semibold"
          >
            Create Account
          </Link>
        </div>
      </div>
    </div>
  )
}

function NotFoundPage() {
  const { user } = useAuthStore()
  const destination = user ? '/u/dashboard' : '/'
  const destinationLabel = user ? 'Go to dashboard' : 'Return home'
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#1B2A4A] bg-cover bg-center bg-no-repeat px-4"
      style={{ backgroundImage: 'url(/auth-bg.jpg)' }}
    >
      <div
        className="w-full max-w-xl text-center bg-[#0F1C2E]/55 backdrop-blur-md border border-white/15 rounded-3xl px-8 py-10 sm:px-12 sm:py-12"
        style={{ boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)' }}
      >
        <p className="text-[14px] tracking-[0.2em] text-[#D4AF5A] font-semibold mb-4">404</p>
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
          Page not found
        </h1>
        <p className="text-[15px] sm:text-base text-white/80 mb-8 leading-relaxed">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to={destination}
          className="inline-block px-8 py-3 bg-[#D4AF5A] text-[#0F1C2E] rounded-full hover:brightness-105 transition-all font-semibold shadow-[0_8px_24px_rgba(15,28,46,0.3)]"
        >
          {destinationLabel}
        </Link>
      </div>
    </div>
  )
}

// ============================================
// USER PAGES (Some placeholders remain)
// ============================================




// ============================================
// MAIN APP COMPONENT
// ============================================

function App() {
  const { initialize, isInitialized } = useAuthStore()

  // Initialize auth on app load
  useEffect(() => {
    if (!isInitialized) {
      initialize()
    }
  }, [initialize, isInitialized])

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/help" element={<HelpPage />} />

        {/* Auth routes (sign-in/up share the moonlit AuthLayout) */}
        <Route element={<AuthLayout />}>
          <Route path="/auth/sign-in" element={<SignInPage />} />
          <Route path="/auth/sign-up" element={<SignUpPage />} />
        </Route>
        {/* Verify/callback have their own watercolor layout (defined in each page) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/verified" element={<EmailVerifiedPage />} />

        {/* Onboarding (protected but doesn't require completed onboarding) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute requireOnboarding={false}>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/support-person"
          element={
            <ProtectedRoute requireOnboarding={false}>
              <SupportPersonOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/paid"
          element={
            <ProtectedRoute requireOnboarding={true}>
              <PaidOnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* User routes (protected, requires onboarding) */}
        <Route
          element={
            <RoleProtectedRoute allowedRoles={['user', 'supporter', 'admin']}>
              <AppLayout />
            </RoleProtectedRoute>
          }
        >
          <Route path="/u/dashboard" element={<DashboardPage />} />
          <Route path="/u/program" element={<ProgrammePage />} />
          <Route path="/u/program/free/:ordinal" element={<FreeVideoPage />} />
          <Route path="/u/assessments" element={<AssessmentsPage />} />
          <Route path="/u/voice-memos" element={<VoiceMemosPage />} />
          <Route path="/u/account" element={<AccountSettingsPage />} />
          <Route path="/u/supporter" element={<SupporterLinkPage />} />
          {/* Legacy alias kept so cached links / browser history don't 404. */}
          <Route path="/u/supporter" element={<Navigate to="/u/supporter" replace />} />
          <Route path="/u/upgrade" element={<UpgradePage />} />
          <Route path="/u/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/u" element={<Navigate to="/u/dashboard" replace />} />
        </Route>

        {/* Supporter routes */}
        <Route
          element={
            <RoleProtectedRoute allowedRoles={['supporter', 'admin']}>
              <AppLayout />
            </RoleProtectedRoute>
          }
        >
          <Route path="/s/dashboard" element={<SupporterDashboardPage />} />
          <Route path="/s/clients" element={<ClientsPage />} />
          <Route path="/s/clients/:clientId" element={<ClientDetailPage />} />
          <Route path="/s/content-library" element={<ContentLibraryPage />} />
          <Route path="/s" element={<Navigate to="/s/dashboard" replace />} />
          {/* Legacy /c/* aliases (pre-supporter-rename) so cached links / bookmarks don't 404. */}
          <Route path="/c" element={<Navigate to="/s/dashboard" replace />} />
          <Route path="/c/dashboard" element={<Navigate to="/s/dashboard" replace />} />
          <Route path="/c/clients" element={<Navigate to="/s/clients" replace />} />
          <Route path="/c/clients/:clientId" element={<Navigate to="/s/clients" replace />} />
          <Route path="/c/content-library" element={<Navigate to="/s/content-library" replace />} />
        </Route>

        {/* Admin routes */}
        <Route
          element={
            <RoleProtectedRoute allowedRoles={['admin']}>
              <AppLayout />
            </RoleProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/videos" element={<AdminVideosPage />} />
          <Route path="/admin/supporters" element={<AdminSupportersPage />} />
          <Route path="/admin/payments" element={<AdminPaymentsPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
        </Route>

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}

export default App
